import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

// S-TABLET-RESPONSIVE-V1 (2026-02-03): Single-File-Tablet-Hardening fuer 769-1199 px.
// Rollback = diese Import-Zeile entfernen + /app/frontend/src/styles/tablet.css loeschen.
import "./styles/tablet.css";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CrmI18nProvider } from "./i18n/CrmI18nContext";

// Shared Components
import { Sidebar } from "./components/shared/Sidebar";
import { roleFallbackPath } from "./components/shared/ProtectedRoute";
import { EmployeeMobileNav } from "./components/employee/EmployeeMobileNav";
import { ActiveStudioBanner } from "./components/shared/ActiveStudioBanner";
import { OperatorStudioSwitcher } from "./components/shared/OperatorStudioSwitcher";
import { EnvironmentBanner } from "./components/shared/EnvironmentBanner";
import { Toaster } from "sonner";
import { getEnvironment, ENV_LIVE, getEnvironmentLabel } from "./utils/environment";

// Lazy-loaded Pages for Ultra Performance
const LoginPage = lazy(() => import("./pages/LoginPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const DailyGoalPage = lazy(() => import("./pages/DailyGoalPage"));
const ErfassungPage = lazy(() => import("./pages/ErfassungPage"));
const GoogleBewertungPage = lazy(() => import("./pages/GoogleBewertungPage"));
const CustomerSearchPage = lazy(() => import("./pages/CustomerSearchPage"));
const CustomerDetailPage = lazy(() => import("./pages/CustomerDetailPage"));
const StatisticsPage = lazy(() => import("./pages/StatisticsPage"));
const EmployeesPage = lazy(() => import("./pages/EmployeesPage"));
const JahresstatistikPage = lazy(() => import("./pages/JahresstatistikPage"));
const ProvisionsregelnPage = lazy(() => import("./pages/ProvisionsregelnPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const PapierkorbPage = lazy(() => import("./pages/PapierkorbPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
// EMPLOYEE-MASTER-AUDIT-DASHBOARD-V1
const AuditDashboardPage = lazy(() => import("./pages/AuditDashboardPage"));
const DataImportPage = lazy(() => import("./pages/DataImportPage"));
const DataReviewPage = lazy(() => import("./pages/DataReviewPage"));
const SystemStatusPage = lazy(() => import("./pages/SystemStatusPage"));
const SoftLaunchPage = lazy(() => import("./pages/SoftLaunchPage"));
const MlCoverageMonitorPage = lazy(() => import("./pages/MlCoverageMonitorPage"));
const HauttypanalyseTabletPage = lazy(() => import("./pages/HauttypanalyseTabletPage"));
const HauttypanalysePage = lazy(() => import("./pages/HauttypanalysePage"));
const IntelligenceDashboardPage = lazy(() => import("./pages/IntelligenceDashboardPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ConfirmConsentPage = lazy(() => import("./pages/ConfirmConsentPage"));
// S-Suntime-WORKFORCE-S1-FOUNDATION-AND-TIMECLOCK-MVP-V1
const TimeClockPage = lazy(() => import("./pages/TimeClockPage"));
// S-Suntime-WORKFORCE-S2-SCHEDULE-FOUNDATION-V1
const SchedulePage = lazy(() => import("./pages/SchedulePage"));
// S-Suntime-WORKFORCE-S6-ABSENCE-AND-MENU-FOUNDATION-V1
const AbsencesPage = lazy(() => import("./pages/AbsencesPage"));
// S-Suntime-WORKFORCE-S6.5-VACATION-LEDGER-AND-TIME-BALANCE-V1
const VacationAccountPage = lazy(() => import("./pages/VacationAccountPage"));
// S-Suntime-WORKFORCE-S6.6-SOLL-IST-TIME-BALANCE-ENGINE-V1
const SollIstPage = lazy(() => import("./pages/SollIstPage"));
const ShiftExchangePage = lazy(() => import("./pages/ShiftExchangePage"));
const CoveragePage = lazy(() => import("./pages/CoveragePage"));
// S-Suntime-WORKFORCE-S11.5-B-BLOCK2-OVERTIME-REQUESTS-AND-POLICIES-V1
const OvertimePage = lazy(() => import("./pages/OvertimePage"));
// S-Suntime-WORKFORCE-MONATSPLAN-V1
const MonthlyPlanPage = lazy(() => import("./pages/MonthlyPlanPage"));
// WORKFORCE-SELF-SERVICE-V1
const MyAreaPage = lazy(() => import("./pages/MyAreaPage"));
const AvailabilityAdminPage = lazy(() => import("./pages/AvailabilityAdminPage"));
const Employee360Page = lazy(() => import("./pages/Employee360Page"));
// WORKFORCE-PAYROLL-HANDOVER-V1
const PayrollHandoverPage = lazy(() => import("./pages/PayrollHandoverPage"));
// WORKFORCE-SHIFT-EXCHANGE-FZ-OVERTIME-OPERATOR-ENGINE-V1
const AdminApprovalsPage = lazy(() => import("./pages/AdminApprovalsPage"));
// WORKFORCE-SHELL-NAVIGATION-UNIFICATION-V1
const WorkforceLayout = lazy(() => import("./components/workforce/WorkforceLayout"));
// EMPLOYEE-MENU-UNIFICATION-V1
const EmployeeLayout = lazy(() => import("./components/employee/EmployeeLayout"));
// ADMIN-MENU-CONSOLIDATION-AND-AUDIT-LAYOUT-FIX-V1
const DataIntegrityLayout = lazy(() => import("./components/admin/DataIntegrityLayout"));
// ARR_EMPLOYEE_MASTER_TRUTH_001 — Mitarbeiterakte (Single Source of Truth)
const EmployeeMasterPage = lazy(() => import("./pages/EmployeeMasterPage"));

// Loading Fallback Component
const PageLoader = () => (
  <div style={{ 
    display: "flex", alignItems: "center", justifyContent: "center",
    height: "100vh", background: "var(--sun-beige)"
  }}>
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 32, fontWeight: 700, color: "var(--sun-gold-dark)", animation: "pulse 1s infinite" }}>
        ☀️
      </div>
      <p style={{ marginTop: 8, color: "var(--sun-text-muted)", fontSize: 13 }}>Laden...</p>
    </div>
  </div>
);

// ─── INSTRUMENTATION: Global Error Logging ───
if (typeof window !== "undefined") {
  window.__SUNTIME_BOOT = Date.now();
  console.log("[SUNTIME] APP_INIT_START", new Date().toISOString());

  window.addEventListener("error", (e) => {
    console.error("[SUNTIME] GLOBAL_ERROR", {
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      time: Date.now() - window.__SUNTIME_BOOT,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    console.error("[SUNTIME] UNHANDLED_PROMISE", {
      reason: e.reason?.message || String(e.reason),
      stack: e.reason?.stack?.split("\n").slice(0, 3).join(" | "),
      time: Date.now() - window.__SUNTIME_BOOT,
    });
  });

  document.addEventListener("visibilitychange", () => {
    console.log("[SUNTIME] VISIBILITY", document.visibilityState, Date.now() - window.__SUNTIME_BOOT);
  });

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      console.warn("[SUNTIME] SW_CONTROLLER_CHANGE — new SW active");
    });
  }

  // S3 — Tab-Title-Prefix fuer Nicht-Live-Umgebungen (Doktrin 2026-04-23).
  // Live laesst den Original-Titel unveraendert.
  try {
    if (getEnvironment() !== ENV_LIVE) {
      const prefix = `[${getEnvironmentLabel()}] `;
      if (typeof document !== "undefined" && document.title && !document.title.startsWith(prefix)) {
        document.title = prefix + document.title;
      }
    }
  } catch (_e) { /* defensiv: Titel-Prefix ist nicht kritisch */ }
}

// ─── Chunk-Error-Erkennung ───
const isChunkLoadError = (error) => {
  if (!error) return false;
  const msg = (error.message || "").toLowerCase();
  const name = (error.name || "").toLowerCase();
  return (
    name === "chunkloaderror" ||
    msg.includes("loading chunk") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    (msg.includes("loading") && msg.includes("chunk") && msg.includes("failed"))
  );
};

// ─── AppErrorBoundary: Robust Recovery + Instrumentation ───
// Strategy:
//   - Chunk errors: 1× auto-retry via reload, then show error with cache-clear
//   - Render errors: Reset boundary to attempt re-render (max 3×), then show error
//   - Full error context logged to console for remote debugging
class AppErrorBoundary extends React.Component {
  static RETRY_KEY = "suntime_chunk_retry";

  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false, error: null, recoveryAttempts: 0 };
    this._clearTimer = null;
    this._recoveryTimer = null;
  }

  componentDidMount() {
    this._clearTimer = setTimeout(() => {
      sessionStorage.removeItem(AppErrorBoundary.RETRY_KEY);
    }, 10000);
    console.log("[SUNTIME] APP_INIT_DONE", Date.now() - (window.__SUNTIME_BOOT || 0));
  }

  componentWillUnmount() {
    if (this._clearTimer) clearTimeout(this._clearTimer);
    if (this._recoveryTimer) clearTimeout(this._recoveryTimer);
  }

  static getDerivedStateFromError(error) {
    const isChunk = isChunkLoadError(error);
    console.error("[SUNTIME] ERROR_BOUNDARY_TRIGGERED", {
      isChunkError: isChunk,
      message: error?.message,
      name: error?.name,
      time: Date.now() - (window.__SUNTIME_BOOT || 0),
    });
    return { hasError: true, isChunkError: isChunk, error };
  }

  componentDidCatch(error, errorInfo) {
    if (this._clearTimer) {
      clearTimeout(this._clearTimer);
      this._clearTimer = null;
    }

    console.error("[SUNTIME] ERROR_DETAILS", {
      error: error?.message,
      stack: error?.stack?.split("\n").slice(0, 5).join("\n"),
      componentStack: errorInfo?.componentStack?.split("\n").slice(0, 5).join("\n"),
    });

    // Chunk errors: 1× auto-retry
    if (this.state.isChunkError) {
      const retried = sessionStorage.getItem(AppErrorBoundary.RETRY_KEY);
      if (!retried) {
        sessionStorage.setItem(AppErrorBoundary.RETRY_KEY, "1");
        console.warn("[SUNTIME] CHUNK_AUTO_RETRY — reloading");
        window.location.reload();
        return;
      }
      return; // Already retried → show error screen
    }

    // Non-chunk (render) errors: Show error immediately (no auto-recovery loop)
    // On mobile, auto-retrying can cause rapid re-renders and instability
    console.error(`[SUNTIME] RENDER_ERROR — showing error screen (no auto-retry)`);
  }

  render() {
    if (!this.state.hasError) {
      // Use recoveryAttempts as key to force remount on recovery
      return (
        <React.Fragment key={this.state.recoveryAttempts}>
          {this.props.children}
        </React.Fragment>
      );
    }

    // Chunk error, first attempt: show loader while auto-reload
    if (this.state.isChunkError && !sessionStorage.getItem(AppErrorBoundary.RETRY_KEY)) {
      return <PageLoader />;
    }

    // After failed recovery or chunk retry: Show error with details
    const errorMsg = this.state.error?.message || "Unbekannter Fehler";
    const isChunk = this.state.isChunkError;

    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: "100vh", background: "var(--sun-beige, #faf6ef)", textAlign: "center",
        padding: 20,
      }}>
        <div style={{ maxWidth: 400 }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>☀️</div>
          <p style={{ fontSize: 16, color: "var(--sun-text-light, #555)", marginBottom: 8 }}>
            {isChunk ? "Seite konnte nicht geladen werden." : "Ein Fehler ist aufgetreten."}
          </p>
          <p style={{
            fontSize: 11, color: "#999", marginBottom: 20,
            fontFamily: "monospace", wordBreak: "break-all",
            background: "#f5f0e8", padding: "8px 12px", borderRadius: 8,
          }}>
            {errorMsg.substring(0, 200)}
          </p>
          <button
            onClick={async () => {
              sessionStorage.removeItem(AppErrorBoundary.RETRY_KEY);
              if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
              }
              if ("serviceWorker" in navigator) {
                const reg = await navigator.serviceWorker.getRegistration();
                if (reg) await reg.update();
              }
              window.location.reload();
            }}
            style={{
              padding: "12px 28px", borderRadius: 10, border: "none",
              background: "var(--sun-gold-dark, #d4a853)", color: "#fff",
              fontSize: 14, fontWeight: 600, cursor: "pointer",
            }}
          >
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }
}

// P2: Prefetch kritischer Chunks im Browser-Idle
if (typeof window !== "undefined") {
  const prefetchChunks = () => {
    import("./pages/DashboardPage").catch(() => {});
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(prefetchChunks);
  } else {
    setTimeout(prefetchChunks, 4000);
  }
}

// Token-Expiry-Warning Banner
const TokenExpiryBanner = () => {
  const { tokenExpiringSoon, logout } = useAuth();
  if (!tokenExpiringSoon) return null;
  return (
    <div data-testid="token-expiry-warning" style={{
      position: "fixed", bottom: 20, left: "50%", transform: "translateX(-50%)",
      zIndex: 9999, display: "flex", alignItems: "center", gap: 12,
      padding: "12px 20px", paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
      borderRadius: 12, width: "calc(100vw - 40px)", maxWidth: 520,
      background: "rgba(245, 158, 11, 0.95)", color: "#fff",
      boxShadow: "0 4px 24px rgba(0,0,0,0.18)", fontSize: 13, fontWeight: 500,
      backdropFilter: "blur(8px)", lineHeight: 1.4
    }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>&#9888;</span>
      <span>Deine Sitzung läuft bald ab. Bitte speichere deine Arbeit und melde dich erneut an.</span>
      <button onClick={logout} data-testid="token-expiry-logout-btn" style={{
        flexShrink: 0, padding: "6px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.4)",
        background: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 12, fontWeight: 600,
        cursor: "pointer", whiteSpace: "nowrap"
      }}>Abmelden</button>
    </div>
  );
};

// U4-3: Re-Login-Modal
const ReLoginModal = () => {
  const { sessionExpired, reLogin, dismissSessionExpired, user } = useAuth();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (sessionExpired && user?.username) {
      setUsername(user.username);
      setPassword("");
      setError("");
    }
  }, [sessionExpired, user?.username]);

  if (!sessionExpired) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await reLogin(username, password);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || "Anmeldung fehlgeschlagen";
      setError(typeof msg === "string" ? msg : "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const overlay = {
    position: "fixed", inset: 0, zIndex: 50000,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
  };
  const card = {
    background: "#fff", borderRadius: 16, padding: "32px 28px", width: "90vw", maxWidth: 380,
    boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
  };
  const inputStyle = {
    width: "100%", padding: "10px 14px", borderRadius: 10,
    border: "1.5px solid #e2e2e2", fontSize: 14, outline: "none", marginBottom: 12,
    boxSizing: "border-box",
  };
  const btnPrimary = {
    width: "100%", padding: "11px", borderRadius: 10, border: "none",
    background: "var(--sun-gold-dark, #d97706)", color: "#fff",
    fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
    opacity: loading ? 0.7 : 1,
  };

  return (
    <div style={overlay} data-testid="relogin-modal-overlay">
      <div style={card} data-testid="relogin-modal" role="dialog" aria-modal="true">
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "var(--sun-gold-dark, #d97706)" }}>
            Sitzung abgelaufen
          </div>
          <p style={{ fontSize: 13, color: "#666", margin: "8px 0 0", lineHeight: 1.5 }}>
            Deine Sitzung ist abgelaufen. Bitte melde dich erneut an, um deine Arbeit fortzusetzen.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#444", display: "block", marginBottom: 4 }}>
            Benutzername
          </label>
          <input
            data-testid="relogin-username" type="text" value={username}
            onChange={(e) => setUsername(e.target.value)} style={inputStyle}
            autoFocus={!username} required
          />
          <label style={{ fontSize: 12, fontWeight: 600, color: "#444", display: "block", marginBottom: 4 }}>
            Passwort
          </label>
          <input
            data-testid="relogin-password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} style={inputStyle}
            autoFocus={!!username} required
          />
          {error && (
            <div data-testid="relogin-error" style={{
              background: "#fef2f2", color: "#b91c1c", padding: "8px 12px",
              borderRadius: 8, fontSize: 12, marginBottom: 12, textAlign: "center",
            }}>{error}</div>
          )}
          <button type="submit" style={btnPrimary} disabled={loading} data-testid="relogin-submit">
            {loading ? "Wird angemeldet..." : "Erneut anmelden"}
          </button>
        </form>
        <button
          onClick={dismissSessionExpired} data-testid="relogin-dismiss"
          style={{
            width: "100%", marginTop: 10, padding: "9px", borderRadius: 10,
            border: "1.5px solid #e2e2e2", background: "transparent",
            color: "#666", fontSize: 13, cursor: "pointer",
          }}
        >Zur Login-Seite</button>
      </div>
    </div>
  );
};

// Protected Route Component
// S-TABLET-RBAC-CUSTOMERS-CONTRACTS-V1: Tablet-Route-Allowlist.
// Tablet darf jetzt — nach Architekt-Direktive — zusaetzlich zur HTA-Kiosk-Page
// die Kundenverwaltung samt Detail/Anwendungskarten/Kontraindikationen
// ansteuern. Backend sperrt alle anderen Domains via Router-Level-Guard
// (forbid_tablet) — das hier ist nur die Frontend-Spiegelung der Wahrheit.
// Pfade werden gegen Prefix-Match gepueft, damit `/customers/:id` auch
// erlaubt ist.
const TABLET_ALLOWED_PATH_PREFIXES = [
  "/tablet/hauttypanalyse",
  "/hauttypenanalyse",   // Desktop-HTA (wird optional auf Tablet auch verfuegbar gemacht)
  "/customers",          // Liste + Detail
  "/erfassung",          // Neukunde anlegen (Architekten-Direktive: JA)
];

const isTabletAllowedPath = (pathname) => {
  return TABLET_ALLOWED_PATH_PREFIXES.some(p =>
    pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p + "?")
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div style={{ fontSize: 28, fontWeight: 700, color: "var(--sun-gold-dark)" }}>
          Suntime No.1
        </div>
        <p style={{ marginTop: 12, color: "var(--sun-text-muted)" }}>Laden...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Tablet-Rolle: nur erlaubte Pfade. Andere -> hartes Redirect auf
  // /hauttypenanalyse (Tablet-Landing-Page, hat Sidebar mit 2 Items).
  if (user.role === "tablet" && !isTabletAllowedPath(location.pathname)) {
    return <Navigate to="/hauttypenanalyse" replace />;
  }
  
  return (
    <>
      <ActiveStudioBanner />
      <div className="fixed top-2 right-4 z-50"><OperatorStudioSwitcher /></div>
      <Sidebar />
      {children}
      {/* UX-2B §23: Bottom-Nav NUR fuer employee (positiver Rollencheck —
          niemals "nicht-admin"; Tablet bleibt unberuehrt, ARR-UX-013). */}
      {user.role === "employee" && <EmployeeMobileNav />}
      <TokenExpiryBanner />
      <ReLoginModal />
    </>
  );
};

// Tablet Route: Auth ohne Sidebar/CRM-Navigation (Kiosk-Modus)
const TabletRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <>
      {children}
      <ReLoginModal />
    </>
  );
};

// Admin Protected Route Component
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to={roleFallbackPath(user)} replace />;
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

// Super Admin Protected Route Component
const SuperAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to={roleFallbackPath(user)} replace />;
  return <ProtectedRoute>{children}</ProtectedRoute>;
};

// Block 5 — Root-/Wildcard-Fallback: rollen-kanonische HOME-Aufloesung.
// Explizites /dashboard bleibt fuer alle Rollen eine gueltige Capability-Route.
const RoleFallback = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={roleFallbackPath(user)} replace />;
};

// Main App Component
function App() {
  return (
    <CrmI18nProvider>
    <AuthProvider>
      <BrowserRouter>
        <EnvironmentBanner />
        <Toaster position="top-center" richColors closeButton />
        <AppErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Route */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Public Verification Routes */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/confirm-consent" element={<ConfirmConsentPage />} />
            
            {/* Protected Routes */}
            <Route path="/erfassung" element={<ProtectedRoute><ErfassungPage /></ProtectedRoute>} />
            <Route path="/google-bewertung" element={<ProtectedRoute><GoogleBewertungPage /></ProtectedRoute>} />
            <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetailPage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><CustomerSearchPage /></ProtectedRoute>} />
            <Route path="/statistics" element={<ProtectedRoute><StatisticsPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/tagesziel" element={<ProtectedRoute><DailyGoalPage /></ProtectedRoute>} />

            <Route path="/provisionsregeln" element={<ProtectedRoute><ProvisionsregelnPage /></ProtectedRoute>} />
            <Route path="/jahresstatistik" element={<ProtectedRoute><JahresstatistikPage /></ProtectedRoute>} />
            <Route path="/intelligence" element={<AdminRoute><IntelligenceDashboardPage /></AdminRoute>} />
            {/* EMPLOYEE-MENU-UNIFICATION-V1 — Mitarbeiter + Mitarbeiterakte unter einer Layout-Route */}
            <Route element={<EmployeeLayout />}>
              {/* WORKFORCE-SELF-SERVICE-RBAC-001 (2026-05-30):
                  Mitarbeiterverwaltung ist Admin-Funktion.
                  Vorher ProtectedRoute -> Mitarbeiter sah komplette
                  Mitarbeiterliste inkl. fremder Identitaeten. */}
              <Route path="/employees" element={<AdminRoute><EmployeesPage /></AdminRoute>} />
              <Route path="/mitarbeiterakte" element={<AdminRoute><EmployeeMasterPage /></AdminRoute>} />
            </Route>
            {/* S-Suntime-WORKFORCE-SHELL-NAVIGATION-UNIFICATION-V1
                Alle 9 Zeiterfassungs-Routen unter gemeinsamer
                Layout-Route mit horizontaler Workforce-Nav-Leiste. */}
            <Route element={<WorkforceLayout />}>
              <Route path="/zeiterfassung" element={<ProtectedRoute><TimeClockPage /></ProtectedRoute>} />
              <Route path="/schichtplan" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
              <Route path="/abwesenheiten" element={<ProtectedRoute><AbsencesPage /></ProtectedRoute>} />
              <Route path="/urlaubskonto" element={<ProtectedRoute><VacationAccountPage /></ProtectedRoute>} />
              <Route path="/soll-ist" element={<ProtectedRoute><SollIstPage /></ProtectedRoute>} />
              <Route path="/schichttausch" element={<ProtectedRoute><ShiftExchangePage /></ProtectedRoute>} />
              {/* RMO-F28a (STUFE 3B): Coverage = betriebliche Planungs-/Auslastungsstrategie
                  → nur Admin/SuperAdmin (Server-RBAC bleibt die Authority). */}
              <Route path="/abdeckung" element={<AdminRoute><CoveragePage /></AdminRoute>} />
              <Route path="/ueberstunden" element={<ProtectedRoute><OvertimePage /></ProtectedRoute>} />
              <Route path="/monatsplan" element={<AdminRoute><MonthlyPlanPage /></AdminRoute>} />
              <Route path="/mein-bereich" element={<ProtectedRoute><MyAreaPage /></ProtectedRoute>} />
              <Route path="/verfuegbarkeit" element={<AdminRoute><AvailabilityAdminPage /></AdminRoute>} />
              <Route path="/mitarbeiter-360" element={<AdminRoute><Employee360Page /></AdminRoute>} />
              <Route path="/lohnabschluss" element={<AdminRoute><PayrollHandoverPage /></AdminRoute>} />
              <Route path="/genehmigungen" element={<AdminRoute><AdminApprovalsPage /></AdminRoute>} />
            </Route>
            {/* ARR_EMPLOYEE_MASTER_TRUTH_001 — Mitarbeiterakte
                EMPLOYEE-MENU-UNIFICATION-V1: Route ist jetzt unter EmployeeLayout (siehe oben) gruppiert. */}
            <Route path="/papierkorb" element={<AdminRoute><PapierkorbPage /></AdminRoute>} />
            {/* ADMIN-MENU-CONSOLIDATION-AND-AUDIT-LAYOUT-FIX-V1 — Datenwahrheit-Monitor mit 4 Tabs */}
            <Route element={<DataIntegrityLayout />}>
              <Route path="/ml-coverage" element={<AdminRoute><MlCoverageMonitorPage /></AdminRoute>} />
              <Route path="/audit-log" element={<AdminRoute><AuditLogPage /></AdminRoute>} />
              <Route path="/audit" element={<AdminRoute><AuditDashboardPage /></AdminRoute>} />
              <Route path="/soft-launch" element={<AdminRoute><SoftLaunchPage /></AdminRoute>} />
            </Route>
            <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />
            
            {/* Super Admin Routes */}
            <Route path="/super-admin" element={<SuperAdminRoute><SuperAdminPage /></SuperAdminRoute>} />
            <Route path="/daten-import" element={<SuperAdminRoute><DataImportPage /></SuperAdminRoute>} />
            <Route path="/daten-pruefung" element={<SuperAdminRoute><DataReviewPage /></SuperAdminRoute>} />
            <Route path="/system-status" element={<SuperAdminRoute><SystemStatusPage /></SuperAdminRoute>} />
            {/* /soft-launch und /ml-coverage sind jetzt unter DataIntegrityLayout gruppiert (siehe oben). */}
            
            {/* Tablet Kiosk Route */}
            <Route path="/tablet/hauttypanalyse" element={<TabletRoute><HauttypanalyseTabletPage /></TabletRoute>} />
            
            {/* Desktop HTA */}
            <Route path="/hauttypenanalyse" element={<ProtectedRoute><HauttypanalysePage /></ProtectedRoute>} />
            
            {/* Default Routes */}
            <Route path="/" element={<RoleFallback />} />
            <Route path="*" element={<RoleFallback />} />
          </Routes>
        </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
    </CrmI18nProvider>
  );
}

export default App;
